/**
 * Checks if the given element is a focusable O2 component.
 * @param element - The element to check.
 * @returns `true` if the element is a focusable O2 component, `false` otherwise.
 */
export function isO2FocusableComponent(element) {
  return (element && element.tagName.toLowerCase() === 'o2-input') ||
    element.tagName.toLowerCase() === 'o2-password' ||
    element.tagName.toLowerCase() === 'o2-textarea' ||
    element.tagName.toLowerCase() === 'o2-select' ||
    element.tagName.toLowerCase() === 'o2-search' ||
    element.tagName.toLowerCase() === 'o2-select-single' ||
    element.tagName.toLowerCase() === 'o2-select-multiple' ||
    element.tagName.toLowerCase() === 'o2-datetime' ||
    element.tagName.toLowerCase() === 'o2-icon-button' ||
    element.tagName.toLowerCase() === 'o2-button'
    ? true
    : false;
}

const DEFAULT_FORM_EVENTS = ['input', 'change', 'focus', 'blur', 'keydown', 'keyup', 'keypress', 'click'];

/**
 * Adds event listeners to stop propagation for form-related events on the given element.
 * @param element - The element to stop event propagation for.
 * @returns A reference to the event handler for cleanup.
 */
export function connectStopPropagateFormEvents(element: HTMLElement): (event: Event) => void {
  const handler = (event: Event) => {
    event.stopPropagation();
  };

  DEFAULT_FORM_EVENTS.forEach(eventType => {
    element.addEventListener(eventType, handler);
  });

  // Return the handler so it can be used for cleanup
  return handler;
}

/**
 * Removes event listeners that stop propagation for form-related events on the given element.
 * @param element - The element to remove event listeners from.
 * @param handler - The event handler to remove.
 */
export function disconnectStopPropagateFormEvents(element: HTMLElement, handler: (event: Event) => void): void {
  DEFAULT_FORM_EVENTS.forEach(eventType => {
    element.removeEventListener(eventType, handler);
  });
}

/**
 * Retrieves the form field ID from the given element's dataset.
 * @param element - The element to retrieve the form field ID from.
 * @returns The form field ID if it exists, otherwise `false`.
 */
export function getFormFieldId(element) {
  const formFieldId = element.dataset?.formFieldId;
  return formFieldId && formFieldId !== '' ? formFieldId : false;
}
